import { useState, useRef } from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import useOutsideClick from '@rooks/use-outside-click';
import { Transition } from '@headlessui/react';

import { PlanEnum } from 'api-bible-shared-lib/utils/types';

import { useApplications } from '../contexts/Applications';
import { usePlans } from '../contexts/Plans';
import { IconDownChevron, IconDefaultApp } from './Icons';
import ImageS3 from './Image';
import Pill from './Pill';
import AppNav from './AppNav';
import ApiKey from './ApiKey';

interface AppHeaderProps {
  fullDetails?: boolean;
  appLinkBasePath: string;
  currentAppId: string;
  currentAppMenuItem: string;
}

export function AppHeader({
  fullDetails = false,
  appLinkBasePath,
  currentAppId,
  currentAppMenuItem,
}: AppHeaderProps) {
  const { applicationsById, defaultApplicationId } = useApplications();
  const app = applicationsById[currentAppId] || applicationsById[defaultApplicationId];

  if (!app) return null;

  return (
    <div className="mb-10 text-white">
      <h3 className="mb-2 text-sm tracking-wide sm:text-base sm:truncate opacity-70">{`ID: ${app.id}`}</h3>
      <AppSelect
        className={fullDetails ? 'mb-5' : ''}
        currentAppId={currentAppId}
        currentAppMenuItem={currentAppMenuItem}
        appLinkBasePath={appLinkBasePath}
      />
      <AppNav currentAppId={currentAppId} hasBilling={app.plan !== PlanEnum.FREE} />
      <ApiKey className={cn({ 'mb-5': fullDetails })} currentAppId={currentAppId} />
    </div>
  );
}

export function AppSelect({
  className,
  currentAppId,
  currentAppMenuItem,
  hideImg = false,
  appLinkBasePath = '',
}: {
  className?: string;
  currentAppId: string;
  currentAppMenuItem: string;
  hideImg?: boolean;
  appLinkBasePath: string;
}) {
  const { plansById } = usePlans();
  const { applicationsById, defaultApplicationId } = useApplications();
  const app = applicationsById[currentAppId] || applicationsById[defaultApplicationId];

  const [ddOpen, setDdOpen] = useState(false);
  const ddRef = useRef(document.createElement('div'));
  useOutsideClick(ddRef, () => setDdOpen(false));

  return (
    <div className={cn('relative', className)} ref={ddRef}>
      <button
        type="button"
        className="flex items-center focus:outline-none"
        onClick={() => setDdOpen(!ddOpen)}
      >
        {!hideImg && (
          <div className="w-20 h-20 mr-5 overflow-hidden rounded-lg">
            {app.iconPath ? (
              <ImageS3
                className="object-cover w-full h-full"
                src={app.iconPath}
                alt="current app icon"
              />
            ) : (
              <IconDefaultApp className="object-cover w-full h-full" />
            )}
          </div>
        )}
        <h2 className="mr-1 text-3xl font-bold leading-7 sm:text-4xl sm:truncate">{app.name}</h2>
        <IconDownChevron className={cn('w-10 h-10 transform', { 'rotate-180': ddOpen })} />
      </button>
      <Transition
        show={ddOpen}
        as="div"
        className="absolute left-0 z-20 p-6 mt-2 bg-white shadow-xl origin-top-left w-80 rounded-2xl box-content"
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <ul className="-mt-3 text-black">
          {Object.values(applicationsById)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((application) => {
              const isActive = application.id === currentAppId;
              return (
                <li key={application.id}>
                  <Link
                    className={cn('flex items-center py-3', {
                      'text-gray-600 hover:text-blue-500': !isActive,
                      'text-blue-500 hover:text-blue-600': isActive,
                    })}
                    onClick={() => setDdOpen(false)}
                    to={`${appLinkBasePath}/${application.id}/${currentAppMenuItem}`}
                  >
                    <div className="inline w-6 h-6 mr-2.5 overflow-hidden rounded">
                      {application.iconPath ? (
                        <ImageS3
                          className="object-cover w-full h-full"
                          src={application.iconPath}
                          key={application.id}
                          alt={`${application.name}'s app icon`}
                        />
                      ) : (
                        <IconDefaultApp
                          key={application.id}
                          className="object-cover w-full h-full"
                        />
                      )}
                    </div>
                    <div className="flex items-center">
                      <span className={cn('mr-2', { 'font-medium': isActive })}>
                        {application.name}
                      </span>
                      <Pill
                        color={plansById[application.plan].color}
                        className="text-white inline-block -mb-px"
                      >
                        {application.plan}
                      </Pill>
                    </div>
                  </Link>
                </li>
              );
            })}
        </ul>
        <div className="w-full h-px mb-3 bg-gray-300" />
        <div>
          <Link className="flex items-center py-3 text-blue-500" to="/application/create">
            <div
              className="inline-block rounded border-2 border-blue-500 w-6 h-6 text-blue-500 text-center text-2xl mr-2.5"
              style={{ lineHeight: 0.75 }}
            >
              +
            </div>
            Create New Application
          </Link>
        </div>
      </Transition>
    </div>
  );
}
